/**
 * Settings resource: TanStack-backed read + update hooks for the per-silo
 * admin settings singleton.
 *
 * `useSettings` is the canonical read — every field lives on one shared
 * cached query, so a screen that needs several settings fields costs one
 * request, not one per field. `useUpdateSettings` PATCHes a subset of fields
 * and invalidates the cached read on success.
 */
import type { Settings } from '@repo/shared/orpc/procedures/settings';
import { useQueryClient } from '@tanstack/react-query';
import { useApiQueryUtils } from 'system/api-client-context';
import { useSiloKey } from 'util/datastore';
import { fireAndForget } from 'util/hooks';

import { useSiloMutation } from './use-silo-mutation';
import { useSiloQuery } from './use-silo-query';

export type { Settings };

export function useSettings(): Settings | undefined {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.settings.get });
    return query.data;
}

/**
 * Update the silo settings singleton. Auto-invalidates `settings.get` on
 * success. Exposes the mutation's `mutateAsync` as `updateSettingsAsync` so
 * call sites don't have to rename it themselves.
 */
export function useUpdateSettings() {
    const queryClient = useQueryClient();
    const utils = useApiQueryUtils();
    const siloKey = useSiloKey();
    const mutation = useSiloMutation({
        procedure: utils.settings.update,
        onSuccess: () => {
            fireAndForget(queryClient.invalidateQueries({ queryKey: utils.settings.get.key({ input: { siloKey } }) }));
        },
    });
    return { ...mutation, updateSettingsAsync: mutation.mutateAsync };
}
